import React, { ReactElement, useState } from "react"
import { v4 as uuid } from "uuid"
import { UploadFile } from "#/components/views/reimbursements/upload/UploadFile"
import { ArrowSubmitButton } from "../shared/layouts"
import { UploadDropzoneWithIllustration } from "#/components/views/reimbursements/upload/UploadDropzoneWithIllustration"
import AttachmentStates from "../../../utils/AttachmentStates"
import Settings from "../../../utils/CarrotConfig"
import { FormattedMessage } from "react-intl"
import { Box, Button, Divider, ForwardIcon } from "@carrotfertility/carotene-core"
import FileCategories from "../../../utils/FileCategories"
import { carrotClient } from "#/utils/CarrotClient"
import { DialogActionsBar } from "@carrotfertility/carotene-core-x"
import { DropEvent, FileRejection } from "react-dropzone"

const MAX_FILE_SIZE = Settings.MAX_FILE_SIZE

export interface AttachmentType {
  file: File
  state: AttachmentStates
  guid: string
  fileCategory: string
}

// FileType is needed so that if a user goes back we can display their uploaded file with a name
export type FileTypes = Array<{ name: string; guid: string; fileCategory: FileCategories }>

type UploadFileProps = {
  fileCategory: FileCategories
  header: ReactElement
  body: ReactElement
  onContinueClick: (attachments: FileTypes) => void
  files: FileTypes
  useArrowButton: boolean
}

const uploadAttachment = async (attachment: AttachmentType, guid: string, fileCategory: string): Promise<string> => {
  const successResponseGuid = await carrotClient.uploadReimbursementFile(attachment.file, guid, fileCategory)
  return successResponseGuid
}

const findAttachmentByGuid = (attachments: AttachmentType[], guid: string): AttachmentType =>
  attachments.find((attachment) => attachment.guid === guid)

const toUploading = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.UPLOADING
  return attachment
}
const toComplete = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.COMPLETED
  return attachment
}
const toFailed = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.FAILED
  return attachment
}

const toVirusDetected = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.VIRUSDETECTED
  return attachment
}

const getFileNameAndGuid = (attachment: AttachmentType): Record<string, string> => ({
  guid: attachment.guid,
  name: attachment.file.name,
  fileCategory: attachment.fileCategory
})

export const UploadFileModalStep = ({
  fileCategory,
  header,
  body,
  onContinueClick,
  files,
  useArrowButton
}: UploadFileProps): JSX.Element => {
  const [attachments, setAttachments] = useState<AttachmentType[]>(() => {
    // if the user has come back to this step we initialize the list of attachments
    // from the file name and guid saved in the reimbursement context. we can assume the state is COMPLETED
    if (files.length) {
      return files.map((file: any) => {
        return {
          file: {
            name: file.name
          },
          state: AttachmentStates.COMPLETED,
          guid: file.guid,
          fileCategory: file.fileCategory ?? fileCategory
        } as AttachmentType
      })
    }
    return []
  })

  const processAcceptedDrop = (files: File[], _event: DropEvent): void => {
    const newAttachments: AttachmentType[] = files.map((file) => ({
      file,
      state: file.size > MAX_FILE_SIZE ? AttachmentStates.OVERSIZED : AttachmentStates.UPLOADING,
      guid: uuid(),
      fileCategory: fileCategory
    }))

    setAttachments((prevAttachments) => [...prevAttachments, ...newAttachments])

    const attachmentsToUpload = newAttachments.filter((a) => a.file.size <= MAX_FILE_SIZE)

    attachmentsToUpload.forEach(async (attachment) => {
      const guid = attachment.guid
      try {
        await uploadAttachment(attachment, guid, fileCategory)
        toComplete(attachment)
      } catch (error) {
        const errorBody = await error?.response?.text()
        if (errorBody === "Virus detected in file upload") {
          toVirusDetected(attachment)
        } else {
          toFailed(attachment)
        }
      } finally {
        setAttachments((attachments) => {
          const index = attachments.findIndex((a) => a.guid === attachment.guid)
          attachments[index] = attachment
          return [...attachments]
        })
      }
    })
  }

  const processRejectedDrop = (files: FileRejection[]): void => {
    const newAttachments: AttachmentType[] = files.map((file) => ({
      file: file.file,
      state: AttachmentStates.INVALIDTYPE,
      guid: uuid(),
      fileCategory: fileCategory
    }))

    setAttachments((prevAttachments) => [...prevAttachments, ...newAttachments])
  }

  // removes the attachment from component state
  const onDelete = (guid: string): void => {
    setAttachments((prevAttachments) => prevAttachments.filter((a) => a.guid !== guid))
  }
  const onRetry = async (guid: string): Promise<void> => {
    const attachment = toUploading(findAttachmentByGuid(attachments, guid))
    try {
      await uploadAttachment(attachment, guid, fileCategory)
      toComplete(attachment)
    } catch {
      toFailed(attachment)
    } finally {
      setAttachments((attachments) => {
        const index = attachments.findIndex((a) => a.guid === attachment.guid)
        attachments[index] = attachment
        return [...attachments]
      })
    }
  }

  return (
    <>
      <Box
        sx={{
          paddingBlockStart: (theme) => theme.tokens.spacing.lg,
          paddingBlockEnd: (theme) => theme.tokens.spacing.xxxl
        }}
      >
        <Box
          sx={{
            paddingBlockEnd: (theme) => theme.tokens.spacing.xxxl
          }}
        >
          {header}
          {body}
        </Box>
        <UploadDropzoneWithIllustration
          small
          onDropAccepted={processAcceptedDrop}
          onDropRejected={processRejectedDrop}
        />
      </Box>
      <Box
        component="ul"
        sx={{
          "> li::before": {
            display: "none"
          },
          display: "flex",
          flexDirection: "column",
          gap: (theme) => theme.tokens.spacing.xs
        }}
      >
        {attachments
          .filter((a) => a.fileCategory === fileCategory)
          .map((attachment) => (
            <Box
              component="li"
              key={attachment.guid}
              sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.tokens.spacing.xs }}
            >
              <Divider />
              <UploadFile attachment={attachment} onDelete={onDelete} onRetry={onRetry} />
            </Box>
          ))}
      </Box>
      <DialogActionsBar
        primaryAction={
          useArrowButton ? (
            <ArrowSubmitButton
              enabled={
                attachments.length > 0 && attachments.filter((a) => a.state !== AttachmentStates.COMPLETED).length === 0
              }
              onClick={() => onContinueClick(attachments.map(getFileNameAndGuid) as FileTypes)}
            >
              <FormattedMessage defaultMessage="Continue" />
            </ArrowSubmitButton>
          ) : (
            <Button
              disabled={
                attachments.length === 0 || attachments.filter((a) => a.state !== AttachmentStates.COMPLETED).length > 0
              }
              onClick={() => onContinueClick(attachments.map(getFileNameAndGuid) as FileTypes)}
              variant="outlined"
              color="secondary"
              endIcon={<ForwardIcon />}
            >
              <FormattedMessage defaultMessage="Continue" />
            </Button>
          )
        }
      />
    </>
  )
}
